import * as React from 'react';
import { useSearchParams } from 'react-router-dom';

import type { SetupStep } from '@/components/setup/experience/lib/setupSteps';
import type { SetupProductsSubStep, SetupSourcePath } from '@/components/setup/experience/types';

/**
 * The guided-setup navigation cursor — the only client-owned state. It lives entirely in the URL
 * query (storeId, step, sub, source) so it survives refreshes and is shareable, and the OAuth
 * round-trip can hand context back through it (`oauth`, `awaiting`, `shop`). No localStorage.
 */

const STEPS: readonly SetupStep[] = ['store', 'products', 'shipping', 'payment', 'checkout', 'complete'];
const SUB_STEPS: readonly SetupProductsSubStep[] = ['choose_source', 'connect', 'manual_product'];
const SOURCES: readonly SetupSourcePath[] = ['shopify', 'woocommerce', 'manual'];

type OAuthStatus = 'success' | 'error';

type SetupParamKey = 'storeId' | 'step' | 'sub' | 'source' | 'oauth' | 'message' | 'awaiting' | 'shop';

export interface SetupNavigation {
  storeId: string | null;
  step: SetupStep | null;
  sub: SetupProductsSubStep | null;
  source: SetupSourcePath | null;
  /** Shopify return marker from the backend callback redirect. */
  oauth: OAuthStatus | null;
  oauthMessage: string | null;
  /** WooCommerce poll marker carried back via the client-controlled wc-auth return_url. */
  awaiting: 'woocommerce' | null;
  shop: string | null;
  isResetRequested: boolean;
  /**
   * Apply several param changes in ONE navigation. Required whenever a transition touches more than
   * one param (e.g. clearing the OAuth markers and changing step together): react-router's
   * setSearchParams reads the current params at call time, so chaining single-param setters would
   * let only the last one win.
   */
  setParams: (patch: Partial<Record<SetupParamKey, string | null>>) => void;
  /** Store just created → enter the products step on the source picker. */
  setStoreCreated: (storeId: string) => void;
  goToStep: (step: SetupStep) => void;
  setSource: (source: SetupSourcePath) => void;
  goToSub: (sub: SetupProductsSubStep) => void;
  goToStore: () => void;
  /** Strip OAuth round-trip markers once they've been handled, keeping the nav cursor. */
  clearOAuthParams: () => void;
  reset: () => void;
}

function parseEnum<T extends string>(value: string | null, allowed: readonly T[]): T | null {
  return value && (allowed as readonly string[]).includes(value) ? (value as T) : null;
}

export function useSetupNavigation(): SetupNavigation {
  const [searchParams, setSearchParams] = useSearchParams();

  const update = React.useCallback(
    (patch: Record<string, string | null>) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          for (const [key, value] of Object.entries(patch)) {
            if (value === null) next.delete(key);
            else next.set(key, value);
          }
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const setStoreCreated = React.useCallback(
    (storeId: string) => update({ storeId, step: 'products', sub: 'choose_source', source: null }),
    [update],
  );
  const goToStep = React.useCallback((step: SetupStep) => update({ step }), [update]);
  const setSource = React.useCallback((source: SetupSourcePath) => update({ source }), [update]);
  const goToSub = React.useCallback((sub: SetupProductsSubStep) => update({ sub }), [update]);
  const goToStore = React.useCallback(() => update({ step: 'store' }), [update]);
  const clearOAuthParams = React.useCallback(
    () => update({ oauth: null, message: null, awaiting: null, shop: null }),
    [update],
  );
  const reset = React.useCallback(() => setSearchParams({}, { replace: true }), [setSearchParams]);

  return {
    storeId: searchParams.get('storeId') || null,
    step: parseEnum(searchParams.get('step'), STEPS),
    sub: parseEnum(searchParams.get('sub'), SUB_STEPS),
    source: parseEnum(searchParams.get('source'), SOURCES),
    oauth: parseEnum(searchParams.get('oauth'), ['success', 'error'] as const),
    oauthMessage: searchParams.get('message') || null,
    awaiting: parseEnum(searchParams.get('awaiting'), ['woocommerce'] as const),
    shop: searchParams.get('shop') || null,
    isResetRequested: searchParams.get('reset') === '1',
    setParams: update,
    setStoreCreated,
    goToStep,
    setSource,
    goToSub,
    goToStore,
    clearOAuthParams,
    reset,
  };
}
